import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { userAPI } from '../services/api';
import './UserPreferences.css';

const UserPreferences = () => {
  const [preferencesList, setPreferencesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPreferences, setEditingPreferences] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formData, setFormData] = useState({
    user_id: '',
    language: 'en',
    currency: 'MYR',
    units: 'metric',
    theme: 'light',
    notifications: true,
    notification_push: true,
    notification_email: true,
    notification_sms: false,
    marketing_opt_in: false,
    profile_visible: true,
    location_sharing: false,
    reviews_public: true,
    font_size: 'medium',
    high_contrast: false,
    voice_navigation: false
  });

  useEffect(() => {
    fetchPreferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAllPreferences(currentPage, 10);
      setPreferencesList(response.data.preferences || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Failed to fetch preferences:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      user_id: '',
      language: 'en',
      currency: 'MYR',
      units: 'metric',
      theme: 'light',
      notifications: true,
      notification_push: true,
      notification_email: true,
      notification_sms: false,
      marketing_opt_in: false,
      profile_visible: true,
      location_sharing: false,
      reviews_public: true,
      font_size: 'medium',
      high_contrast: false,
      voice_navigation: false
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPreferences) {
        await userAPI.updatePreferences(editingPreferences.user_id, formData);
      } else {
        await userAPI.createPreferences(formData);
      }
      
      setShowModal(false);
      setEditingPreferences(null);
      resetForm();
      fetchPreferences();
      alert(editingPreferences ? 'Preferences updated successfully!' : 'Preferences created successfully!');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      alert('Failed to save preferences. Please try again.');
    }
  };

  const handleEdit = (preferences) => {
    setEditingPreferences(preferences);
    setFormData({
      user_id: preferences.user_id,
      language: preferences.language || 'en',
      currency: preferences.currency || 'MYR',
      units: preferences.units || 'metric',
      theme: preferences.theme || 'light',
      notifications: preferences.notifications || false,
      notification_push: preferences.notification_push || false,
      notification_email: preferences.notification_email || false,
      notification_sms: preferences.notification_sms || false,
      marketing_opt_in: preferences.marketing_opt_in || false,
      profile_visible: preferences.profile_visible || false,
      location_sharing: preferences.location_sharing || false,
      reviews_public: preferences.reviews_public || false,
      font_size: preferences.font_size || 'medium',
      high_contrast: preferences.high_contrast || false,
      voice_navigation: preferences.voice_navigation || false
    });
    setShowModal(true);
  };

  const handleDelete = async (userId) => {
    if (window.confirm('Are you sure you want to delete these preferences?')) {
      try {
        await userAPI.deletePreferences(userId);
        fetchPreferences();
        alert('Preferences deleted successfully!');
      } catch (error) {
        console.error('Failed to delete preferences:', error);
        alert('Failed to delete preferences. Please try again.');
      }
    }
  };

  const getBooleanBadge = (value) => {
    return (
      <span className={`status-badge ${value ? 'enabled' : 'disabled'}`}>
        {value ? 'Enabled' : 'Disabled'}
      </span>
    );
  };

  return (
    <Layout title="User Preferences Management">
      <div className="user-preferences">
        <div className="preferences-header">
          <h2>User Preferences Management</h2>
          <p>Manage user preferences and settings</p>
          <button 
            className="add-btn"
            onClick={() => {
              setEditingPreferences(null);
              resetForm();
              setShowModal(true);
            }}
          >
            Add New Preferences
          </button>
        </div>

        {loading ? (
          <div className="loading">Loading preferences...</div>
        ) : (
          <div className="preferences-table">
            <table>
              <thead>
                <tr>
                  <th>User ID</th>
                  <th>Language</th>
                  <th>Currency</th>
                  <th>Theme</th>
                  <th>Notifications</th>
                  <th>Privacy</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {preferencesList.length > 0 ? (
                  preferencesList.map((prefs) => (
                    <tr key={prefs.user_id}>
                      <td>
                        <span className="user-id-badge">{prefs.user_id}</span>
                      </td>
                      <td>
                        <div className="preference-details">
                          <strong>{prefs.language?.toUpperCase() || 'EN'}</strong>
                          <small>{prefs.units || 'metric'}</small>
                        </div>
                      </td>
                      <td>
                        <span className="currency-badge">{prefs.currency || 'MYR'}</span>
                      </td>
                      <td>
                        <span className={`theme-badge ${prefs.theme || 'light'}`}>
                          {prefs.theme || 'Light'}
                        </span>
                      </td>
                      <td>
                        <div className="notification-status">
                          <div>Push: {getBooleanBadge(prefs.notification_push)}</div>
                          <div>Email: {getBooleanBadge(prefs.notification_email)}</div>
                        </div>
                      </td>
                      <td>
                        <div className="privacy-status">
                          <div>Profile: {getBooleanBadge(prefs.profile_visible)}</div>
                          <div>Location: {getBooleanBadge(prefs.location_sharing)}</div>
                        </div>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="edit-btn"
                            onClick={() => handleEdit(prefs)}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDelete(prefs.user_id)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="no-data">
                      No preferences found. Click "Add New Preferences" to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                <span>Page {currentPage} of {totalPages}</span>
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}

        {/* Modal for Add/Edit */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>{editingPreferences ? 'Edit User Preferences' : 'Add New User Preferences'}</h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowModal(false)}
                >
                  ×
                </button>
              </div>
              
              <form onSubmit={handleSubmit}>
                {/* Basic Settings */}
                <div className="form-section">
                  <h4>Basic Settings</h4>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>User ID *</label>
                      <input
                        type="number"
                        name="user_id"
                        value={formData.user_id}
                        onChange={handleInputChange}
                        required
                        disabled={editingPreferences}
                      />
                    </div>

                    <div className="form-group">
                      <label>Language</label>
                      <select
                        name="language"
                        value={formData.language}
                        onChange={handleInputChange}
                      >
                        <option value="en">English</option>
                        <option value="ms">Bahasa Malaysia</option>
                        <option value="zh">中文</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Currency</label>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleInputChange}
                      >
                        <option value="MYR">Malaysian Ringgit (MYR)</option>
                        <option value="USD">US Dollar (USD)</option>
                        <option value="EUR">Euro (EUR)</option>
                        <option value="SGD">Singapore Dollar (SGD)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Units</label>
                      <select
                        name="units"
                        value={formData.units}
                        onChange={handleInputChange}
                      >
                        <option value="metric">Metric (km, °C)</option>
                        <option value="imperial">Imperial (miles, °F)</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Theme</label>
                      <select
                        name="theme"
                        value={formData.theme}
                        onChange={handleInputChange}
                      >
                        <option value="light">Light</option>
                        <option value="dark">Dark</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Font Size</label>
                      <select
                        name="font_size"
                        value={formData.font_size}
                        onChange={handleInputChange}
                      >
                        <option value="small">Small</option>
                        <option value="medium">Medium</option>
                        <option value="large">Large</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Notification Settings */}
                <div className="form-section">
                  <h4>Notification Settings</h4>
                  <div className="checkbox-grid">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="notifications"
                        checked={formData.notifications}
                        onChange={handleInputChange}
                      />
                      General Notifications
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="notification_push"
                        checked={formData.notification_push}
                        onChange={handleInputChange}
                      />
                      Push Notifications
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="notification_email"
                        checked={formData.notification_email}
                        onChange={handleInputChange}
                      />
                      Email Notifications
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="notification_sms"
                        checked={formData.notification_sms}
                        onChange={handleInputChange}
                      />
                      SMS Notifications
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="marketing_opt_in"
                        checked={formData.marketing_opt_in}
                        onChange={handleInputChange}
                      />
                      Marketing Communications
                    </label>
                  </div>
                </div>

                {/* Privacy & Accessibility */}
                <div className="form-section">
                  <h4>Privacy & Accessibility</h4>
                  <div className="checkbox-grid">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="profile_visible"
                        checked={formData.profile_visible}
                        onChange={handleInputChange}
                      />
                      Public Profile
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="location_sharing"
                        checked={formData.location_sharing}
                        onChange={handleInputChange}
                      />
                      Location Sharing
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="reviews_public"
                        checked={formData.reviews_public}
                        onChange={handleInputChange}
                      />
                      Public Reviews
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="high_contrast"
                        checked={formData.high_contrast}
                        onChange={handleInputChange}
                      />
                      High Contrast Mode
                    </label>

                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="voice_navigation"
                        checked={formData.voice_navigation}
                        onChange={handleInputChange}
                      />
                      Voice Navigation
                    </label>
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="save-btn">
                    {editingPreferences ? 'Update Preferences' : 'Create Preferences'}
                  </button>
                  <button 
                    type="button" 
                    className="cancel-btn"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default UserPreferences;